import { useState, useRef, useEffect, useMemo } from "react";

import moment from "moment-timezone";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { Bar, Chart } from "react-chartjs-2";

//Backend access
import { loadToken, getCameraHistory } from 'api/bks';
import PropTypes from "prop-types";

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import CameraImage from "nextus/CameraImage";
import { PacmanLoader } from "react-spinners";
import { Card, Slider, ThemeProvider } from "@mui/material";

import ClearwayOverlay from '../../../../nextus-assets/images/clearway_overlay.png';
import { createTheme } from '@mui/material/styles';
import SoftTypography from "components/SoftTypography";

import theme from "assets/theme";
import { difference } from "@turf/turf";
const { palette, boxShadows, functions, transitions, breakpoints, borders } = theme;

const sliderTheme = createTheme({
    palette: {
        primary: {
            light: '#757ce8',
            main: '#3f50b5',
            dark: '#002884',
            contrastText: '#fff',
        },
        secondary: {
            light: '#ff7961',
            main: '#f44336',
            dark: '#ba000d',
            contrastText: '#000',
        },
    },
});

// Images
function CameraFeed({ uuid, currentTime, setCurrentTime, startTime, setStartTime, endTime, setEndTime, cameraData, isLoading, current_camera_url }) {
    const [selectedImage, setSelectedImage] = useState({
        index: cameraData[Number(cameraData.length) - 1] ? Number(cameraData.length) - 1 : -1,
        _id: cameraData[Number(cameraData.length) - 1] ? cameraData[Number(cameraData.length) - 1]._id : null,
        time_displayed: cameraData[Number(cameraData.length) - 1] ? cameraData[Number(cameraData.length) - 1].time_displayed : moment().subtract(1, 'day').unix(),
        time_changed: cameraData[Number(cameraData.length) - 1] ? cameraData[Number(cameraData.length) - 1].time_changed : moment().unix()
    });

    loadToken(useAuth0());

    function nextImage(currentTime, key_used = false) {
        var new_image = {
            index: selectedImage.index,
            _id: selectedImage._id,
            time_displayed: selectedImage.time_displayed,
            time_changed: selectedImage.time_changed,
        }

        if (key_used) {
            new_image.index++;
        }
        else {
            for (var image_index in cameraData) {
                if (currentTime >= cameraData[image_index].time_displayed && currentTime <= cameraData[image_index].time_changed) {
                    new_image.index = Number(image_index);
                    break;
                }
            }
        }

        if (new_image.index <= -1 || (cameraData.length && new_image.index >= cameraData.length)) {
            new_image.index = cameraData.length - 1;
        }

        if (typeof cameraData != 'undefined' && typeof cameraData[new_image.index] != 'undefined') {
            new_image._id = cameraData[new_image.index]._id;
            new_image.time_displayed = cameraData[new_image.index].time_displayed;
            new_image.time_changed = cameraData[new_image.index].time_changed;
        }

        if (key_used) {
            // console.log('key next: ', new_image.time_displayed)
            setCurrentTime(new_image.time_displayed);
        }

        setSelectedImage(new_image);
    }

    function previousImage(currentTime, key_used = false) {
        var new_image = {
            index: selectedImage.index,
            _id: selectedImage._id,
            time_displayed: selectedImage.time_displayed,
            time_changed: selectedImage.time_changed,
        };

        if (key_used) {
            new_image.index--;
        }
        else {
            for (var image_index in cameraData) {
                if (currentTime >= cameraData[image_index].time_displayed && currentTime <= cameraData[image_index].time_changed) {
                    new_image.index = Number(image_index);
                    break;
                }
            }
        }

        if (new_image.index <= -1 || new_image.index >= cameraData.length) {
            if (selectedImage.index == 0 && new_image.index <= -1) {
                new_image.index = 0;
            }
            else {
                new_image.index = cameraData.length - 1;
            }
        }

        if (typeof cameraData != 'undefined' && typeof cameraData[new_image.index] != 'undefined') {
            new_image._id = cameraData[new_image.index]._id;
            new_image.time_displayed = cameraData[new_image.index].time_displayed;
            new_image.time_changed = cameraData[new_image.index].time_changed;
        }

        if (key_used) {
            // console.log('key prev: ', new_image.time_changed)
            setCurrentTime(new_image.time_changed);
        }

        setSelectedImage(new_image);
    }

    function handleKeyDown(e) {
        // arrow up/down button should select next/previous list element
        if (e.keyCode === 37) {
            previousImage(null, true);
        } else if (e.keyCode === 39) {
            nextImage(null, true);
        }
    }

    function valueLabelFormat(value) {
        return `${moment(value * 1000).format('hh:mmA, DD/MM/YYYY')}`;
    }

    const handleChange = (e, newValue) => {
        if (newValue < selectedImage.time_displayed) {
            // console.log('handle change prev image')
            previousImage(newValue);
        }
        else if (newValue > selectedImage.time_changed) {
            // console.log('handle change next image')
            nextImage(newValue);
        }

        setCurrentTime(newValue);
    }

    function calculateTimeDiff() {

        var start = moment(startTime * 1000);
        var end = moment(endTime * 1000);
        var res = "";

        var difference = 0;

        if (end.diff(start, 'hours') > 23) {
            difference = end.diff(start, 'days');
            res = difference == 1 ? `< ${difference} day >` : `< ${difference} days >`;
        }

        if (end.diff(start, 'days') > 6 && end.diff(start, 'days') % 7) {
            difference = end.diff(start, 'days');
            var weeks = Math.floor(difference / 7);

            if (weeks == 1) {
                res = difference % 7 == 1 ? `< ${weeks} week, ${difference % 7} day >` : `< ${weeks} week, ${difference % 7} days >`;
            }
            else {
                res = difference % 7 == 1 ? `< ${weeks} weeks, ${difference % 7} day >` : `< ${weeks} weeks, ${difference % 7} days >`;
            }
        }
        else if (end.diff(start, 'days') > 6) {
            difference = end.diff(start, 'weeks');
            res = difference == 1 ? `< ${difference} week >` : `< ${difference} weeks >`;
        }

        return res;
    }

    const dateSlider = (
        <Card sx={{ border: '1px solid #ccc' }}>
            {window.innerWidth > breakpoints.values.x5l ? (
                <SoftBox px={2} py={5} textAlign="center">
                    <Grid container>
                        <Grid item xxs={12} x5l={3} x6l={2.5}>
                            <SoftBox display="flex" justifyContent="center" alignItems="center">
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} disabled />
                                        ) : (
                                            <DatePicker value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} />
                                        )}

                                    </LocalizationProvider>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} x5l={6} x6l={7}>
                            <SoftBox display="flex" justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
                                <ThemeProvider theme={sliderTheme}>
                                    {isLoading ? (
                                        <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime} disabled />
                                    ) : (
                                        <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime} />
                                    )}
                                </ThemeProvider>
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} x5l={3} x6l={2.5}>
                            <SoftBox display="flex" justifyContent="center" alignItems="center">
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} disabled />
                                        ) : (
                                            <DatePicker value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} />
                                        )}
                                    </LocalizationProvider>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                    </Grid>
                </SoftBox>
            ) : (
                <SoftBox px={2} py={5} textAlign="center">
                    <Grid container>
                        <Grid item xxs={4.5}>
                            <SoftBox display="flex" justifyContent="flex-start" alignItems="center">
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} disabled />
                                        ) : (
                                            <DatePicker value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} />
                                        )}

                                    </LocalizationProvider>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={3}>
                            <SoftBox mt={1}>
                                <SoftTypography variant="h6">
                                    {calculateTimeDiff()}
                                </SoftTypography>
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={4.5}>
                            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} disabled />
                                        ) : (
                                            <DatePicker value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} />
                                        )}
                                    </LocalizationProvider>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                        {/* <Grid item xxs={1} /> */}
                        <Grid item xxs={12}>
                            <SoftBox display="flex" pt={1} justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
                                <ThemeProvider theme={sliderTheme}>
                                    {isLoading ? (
                                        <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime} disabled />
                                    ) : (
                                        <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime} />
                                    )}
                                </ThemeProvider>
                            </SoftBox>
                        </Grid>
                    </Grid>
                </SoftBox>
            )}
        </Card>
    );

    const loading = (<>
        <Grid item xxs={12}>
            <SoftBox sx={{ height: "50vh", width: "100%" }}>
                <SoftBox sx={{ height: "100%" }}>
                    <Grid container sx={{ height: "100%" }}>
                        <Grid item xxs={12} sm={12} md={12} lg={12} xl={12} sx={{ height: "100%" }}>
                            <SoftBox sx={{ 'object-fit': "contain", textAlign: 'center', height: "100%", width: "100%" }} >
                                <SoftBox
                                    component="img"
                                    src={current_camera_url}
                                    position="relative"
                                    maxWidth="100%"
                                    height="100%"
                                    borderRadius="lg"
                                />
                            </SoftBox>
                        </Grid>
                    </Grid>
                </SoftBox>
            </SoftBox>
        </Grid>
        <Grid item xxs={12}>
            <SoftBox mt={5} mb={2} sx={{ width: "100%" }}>
                {dateSlider}
            </SoftBox>
        </Grid>
    </>);

    const rendered = (<>
        <Grid item xxs={12}>
            <SoftBox sx={{ height: "50vh", width: "100%" }}>
                {selectedImage._id && <CameraImage image_data={selectedImage} />}
            </SoftBox>
        </Grid>
        <Grid item xxs={12}>
            <SoftBox mt={5} mb={2} sx={{ width: "100%" }}>
                {dateSlider}
            </SoftBox>
        </Grid>
    </>);

    return (
        <Card>
            <SoftBox p={2}>
                <Grid container sx={{ maxHeight: '850px' }} onKeyDown={handleKeyDown}>
                    {isLoading && loading}
                    {!isLoading && rendered}
                </Grid>
            </SoftBox>
        </Card>
    );
}

// Typechecking props for the CameraFeed
CameraFeed.propTypes = {
    uuid: PropTypes.string,
    currentTime: PropTypes.number,
    setCurrentTime: PropTypes.func,
    startTime: PropTypes.number,
    setStartTime: PropTypes.func,
    endTime: PropTypes.number,
    setEndTime: PropTypes.func,
    cameraData: PropTypes.array,
    isLoading: PropTypes.bool,
    current_camera_url: PropTypes.string,
};


export default CameraFeed;
